import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ExternalLink } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { SeverityBadgeLarge } from './SeverityBadgeLarge';
import { useFraudHubCriticalBoard } from '@/api/hooks/useFraudHubCriticalBoard';
import { formatAlertTime, shortToken } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { TransactionKind } from '@/types/alerts';
import type { CriticalBoardAlert } from '@/types/fraudHub';

const REFRESH_INTERVAL_SECONDS = 60;

const KIND_BADGE: Record<TransactionKind, string> = {
  fuel: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  oil: 'bg-amber-50 text-amber-700 border-amber-200',
  toll: 'bg-violet-50 text-violet-700 border-violet-200',
};

const KINDS: { key: TransactionKind; label: string }[] = [
  { key: 'fuel', label: 'Fuel' },
  { key: 'oil', label: 'Oil' },
  { key: 'toll', label: 'Toll' },
];

/**
 * Section 1 of the Fraud Hub: a dense Critical-only alerts board.
 *
 * Server returns the top 25 open Critical alerts PER KIND, joined to
 * station/merchant for one-line context, plus `counts_by_kind` — the
 * true open-Critical DB total per kind, which is what the tab badges
 * render (the fetched rows are a sample). Auto-refreshes every 60 s
 * (TanStack `refetchInterval`). The "Last updated Xs ago" header
 * resets on every successful fetch so investigators can trust live-
 * ness at a glance.
 *
 * The board ALWAYS shows only Critical (server-side filter); the
 * severity column is kept on purpose — it's a deliberate visual cue
 * that this is the Critical zone, not an accidental redundancy.
 */
export function CriticalAlertsBoard(): JSX.Element {
  const { data, isLoading, isError, error, isFetching, dataUpdatedAt } =
    useFraudHubCriticalBoard();

  // Tick once a second to drive the "Xs ago" countdown.
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const secondsSinceUpdate =
    dataUpdatedAt > 0 ? Math.max(0, Math.floor((now - dataUpdatedAt) / 1000)) : 0;

  const alerts = data?.alerts ?? [];
  const totalOpen = data?.total_critical_open ?? alerts.length;
  const truncated = totalOpen > alerts.length;

  // True open-Critical DB totals per kind — drives the tab badges. The
  // fetched `alerts` array is only a per-kind sample and must never be
  // counted for display.
  const countsByKind = data?.counts_by_kind;

  // Split the fetched alerts into Fuel / Oil / Toll tabs by transaction
  // kind. These slices feed the TABLE BODY only, never a count shown to
  // the user.
  const byKind = useMemo(
    () => ({
      fuel: alerts.filter((a) => a.transaction_kind === 'fuel'),
      oil: alerts.filter((a) => a.transaction_kind === 'oil'),
      toll: alerts.filter((a) => a.transaction_kind === 'toll'),
    }),
    [alerts],
  );

  // Land on the kind with the largest true DB count once data arrives;
  // respect the user's pick after.
  const [tab, setTab] = useState<TransactionKind>('fuel');
  const didInitTab = useRef(false);
  useEffect(() => {
    if (didInitTab.current || !countsByKind) return;
    const top = (['fuel', 'oil', 'toll'] as TransactionKind[]).reduce(
      (best, k) => ((countsByKind[k] ?? 0) > (countsByKind[best] ?? 0) ? k : best),
      'fuel' as TransactionKind,
    );
    setTab(top);
    didInitTab.current = true;
  }, [countsByKind]);

  return (
    <Card className="border-red-300 bg-red-50/30">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0 pb-3">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Critical Alerts</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Open critical alerts — sorted newest first.
            {truncated && (
              <>
                {' '}
                Showing {alerts.length} of {totalOpen.toLocaleString('en-ZA')} open.
              </>
            )}
          </p>
        </div>
        <RefreshIndicator
          secondsSinceUpdate={secondsSinceUpdate}
          isFetching={isFetching}
          hasData={dataUpdatedAt > 0}
        />
      </CardHeader>

      <CardContent className="pt-0">
        {isLoading && <BoardSkeleton />}

        {!isLoading && isError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            Failed to load Critical alerts: {error?.message ?? 'unknown error'}
          </div>
        )}

        {!isLoading && !isError && alerts.length === 0 && <AllClearPanel />}

        {!isLoading && !isError && alerts.length > 0 && (
          <Tabs value={tab} onValueChange={(v) => setTab(v as TransactionKind)}>
            <TabsList>
              {KINDS.map(({ key, label }) => (
                <TabsTrigger key={key} value={key} className="gap-1.5">
                  {label}
                  <span className="rounded bg-muted-foreground/15 px-1.5 text-xs tabular-nums">
                    {(countsByKind?.[key] ?? 0).toLocaleString('en-ZA')}
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
            {KINDS.map(({ key, label }) => {
              const rows = byKind[key];
              const kindTotal = countsByKind?.[key] ?? 0;
              return (
                <TabsContent key={key} value={key}>
                  {rows.length > 0 ? (
                    <>
                      <AlertsTable alerts={rows} />
                      {kindTotal > rows.length && (
                        <p className="mt-2 text-xs text-muted-foreground">
                          Showing {rows.length} of{' '}
                          {kindTotal.toLocaleString('en-ZA')} open.
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="rounded-md border bg-background px-4 py-8 text-center text-sm text-muted-foreground">
                      No open critical {label.toLowerCase()} alerts.
                    </div>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
}

function AlertsTable({ alerts }: { alerts: CriticalBoardAlert[] }): JSX.Element {
  return (
    <div className="overflow-hidden rounded-md border bg-background">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[140px]">Time</TableHead>
            <TableHead className="w-[120px]">Severity</TableHead>
            <TableHead className="w-[100px]">Rule</TableHead>
            <TableHead className="w-[80px] text-right">Risk</TableHead>
            <TableHead className="w-[120px]">Vehicle</TableHead>
            <TableHead>Context</TableHead>
            <TableHead className="w-[80px]">Kind</TableHead>
            <TableHead className="w-[120px] text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {alerts.map((a) => {
            const context = a.station_name ?? a.merchant_name ?? '—';
            return (
              <TableRow key={a.id}>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {formatAlertTime(a.alert_time)}
                </TableCell>
                <TableCell>
                  <SeverityBadgeLarge severity="Critical" />
                </TableCell>
                <TableCell className="font-mono text-sm">{a.rule_id}</TableCell>
                <TableCell className="text-right font-semibold tabular-nums">
                  {a.risk_score}
                </TableCell>
                <TableCell
                  className={cn(
                    'font-mono text-xs',
                    a.registration ? '' : 'text-muted-foreground',
                  )}
                >
                  {a.registration ?? shortToken(a.asset_token) ?? '—'}
                </TableCell>
                <TableCell className="max-w-[280px] truncate" title={context}>
                  {context}
                </TableCell>
                <TableCell>
                  {a.transaction_kind ? (
                    <Badge
                      variant="outline"
                      className={cn('text-xs font-medium', KIND_BADGE[a.transaction_kind])}
                    >
                      {a.transaction_kind}
                    </Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Link
                    to={`/fraud/${a.id}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  >
                    investigate
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

interface RefreshIndicatorProps {
  secondsSinceUpdate: number;
  isFetching: boolean;
  hasData: boolean;
}

function RefreshIndicator({
  secondsSinceUpdate,
  isFetching,
  hasData,
}: RefreshIndicatorProps): JSX.Element {
  if (!hasData) {
    return (
      <span className="shrink-0 text-xs text-muted-foreground">Loading…</span>
    );
  }
  // Show "Refreshing…" for the brief window TanStack is in-flight.
  if (isFetching) {
    return (
      <span className="shrink-0 text-xs font-medium text-primary">
        Refreshing…
      </span>
    );
  }
  const ttl = Math.max(0, REFRESH_INTERVAL_SECONDS - secondsSinceUpdate);
  return (
    <span
      className="shrink-0 text-xs tabular-nums text-muted-foreground"
      title={`Auto-refresh in ${ttl}s`}
    >
      Last updated {secondsSinceUpdate}s ago
    </span>
  );
}

function BoardSkeleton(): JSX.Element {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

function AllClearPanel(): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50/60 px-4 py-10 text-center">
      <CheckCircle2 className="h-12 w-12 text-emerald-600" aria-hidden />
      <div>
        <p className="text-lg font-semibold text-emerald-900">All clear</p>
        <p className="mt-1 text-sm text-emerald-800/80">
          No open critical alerts at this moment. The board refreshes every
          60&nbsp;seconds.
        </p>
      </div>
    </div>
  );
}

export default CriticalAlertsBoard;
